import { useState } from 'react'
import { CheckCircle, Minus, Plus } from '@phosphor-icons/react'
import { useActor } from '@/auth/pin/phoneSession'
import { useFormat, useI18n } from '@/i18n'
import { newId } from '@/lib/ids'
import { enqueueCommand, enqueueCommands, type EnqueueInput } from '@/offline'
import {
  Banner,
  BilingualLabel,
  Button,
  Card,
  CardBody,
  CardHeader,
  ChoiceGroup,
  Field,
  IntegerInput,
  KgInput,
  PhotoCapture,
  ScanField,
  useToast,
  type ScanMeta,
} from '@/ui'
import type { RecordOutputPayload, WeighInPayload } from '@/features/process/commands'
import { runParentIds, type BagView, type PackRunView, type ProcessState } from '@/features/process/projection'
import { checkFreshCode, classifyCode } from '@/features/process/model/rules'
import { Counter, Line, ReadingField, ScanButton, type Reading } from '@/features/process/components/bits'
import { useBoth, useRefusal } from '@/features/process/components/useText'
import { useNow } from '@/features/process/useProcess'
import type { VerifyPouchPayload, WeighPouchesPayload } from '../../commands'
import '../../commands'
import { checkPouchText, fillDayOver, isBelowMinimum, lowShare, minPouchWeight, spotWeightDue } from '../../model/reconcile'
import { checkPackSack, sackNeedsSecondEntry } from '../../model/scan'
import type { FillDraft, FillUpdate, TagScan } from './fillDraft'

/**
 * F7.3 — Fill and weigh: sacks of the batch into the hopper, the first pouch against the lot's
 * QR link, the pouch count, spot weights on the schedule, the last pouch, and the leftover and
 * spillage sacks. Everything is a command in the outbox; the count travels with the close.
 */
export function FillSection({
  state,
  run,
  fill,
  update,
  held,
}: {
  state: ProcessState
  run: PackRunView
  fill: FillDraft
  update: FillUpdate
  held: boolean
}) {
  const { t } = useI18n()
  const fmt = useFormat()
  const toast = useToast()
  const actor = useActor()
  const refusal = useRefusal()
  const both = useBoth()
  const now = useNow()
  const lot = run.pack_lot!
  const sku = run.sku
  const dayOver = fillDayOver(lot.pack_date, now)
  const minG = minPouchWeight(sku.net_g, sku.tolerance_g)
  const inputG = run.inputs.reduce((a, i) => a + (i.weight_g ?? 0), 0)
  const issued = run.issued_container_ids.length
  const filling = !!run.first_pouch?.match
  const positions = run.spot_weights.map((w) => w.position)
  const due = filling ? spotWeightDue(fill.pouches_good, run.spot_every, positions) : null
  const lowPct = lowShare(
    run.spot_weights.map((w) => w.value_g),
    sku.net_g,
    sku.tolerance_g,
  )
  const stopLine = run.spot_weights.length >= 5 && lowPct > state.pack.settings.low_pouch_stop_pct
  const countLocked = !filling || fill.pending_reject || due !== null || dayOver

  const isKnownTag = (c: string) => classifyCode(state, c).kind !== 'UNKNOWN'

  // ---- sack in ----
  const [sack, setSack] = useState<{ bag: BagView; qr: string; entry: ScanMeta['entry'] } | null>(null)
  const [sackReading, setSackReading] = useState<Reading | null>(null)
  const sackIn = async () => {
    if (!sack || !sackReading) return
    try {
      const id = newId()
      const payload: WeighInPayload = {
        run_input_id: id,
        run_id: run.id,
        qr_payload: sack.qr,
        entry: sack.entry,
        container_id: sack.bag.container_id,
        weight_read_g: sackReading.read_g,
        issue_seen: true,
        confirmed_twice: sackReading.confirmed_twice,
      }
      await enqueueCommand({ id, type: 'weigh_in', payload, actorId: actor.user_id, parentIds: runParentIds(run) })
      toast.success(t('pack.fill.sackSaved', { tag: sack.qr }))
      setSack(null)
      setSackReading(null)
    } catch (e) {
      toast.error(t('process.saveFailed'), e instanceof Error ? e.message : undefined)
    }
  }

  // ---- first / last pouch ----
  const [pouchText, setPouchText] = useState<{ position: 'FIRST' | 'LAST'; text: string } | null>(null)
  const [pouchPhoto, setPouchPhoto] = useState<string | null>(null)
  const verify = async (position: 'FIRST' | 'LAST') => {
    if (!pouchText || pouchText.position !== position || !pouchPhoto) return
    const result = checkPouchText(pouchText.text, lot.qr_url, isKnownTag)
    try {
      const payload: VerifyPouchPayload = {
        run_id: run.id,
        pack_lot_id: lot.id,
        position,
        scanned_text: pouchText.text,
        match: result === 'MATCH',
        photo_id: pouchPhoto,
      }
      await enqueueCommand({
        type: 'verify_pouch',
        payload,
        actorId: actor.user_id,
        mediaIds: [pouchPhoto],
        parentIds: runParentIds(run),
      })
      if (result === 'MATCH') toast.success(t('pack.fill.pouchMatch'))
      else toast.error(t('pack.fill.pouchMismatch'))
      setPouchText(null)
      setPouchPhoto(null)
    } catch (e) {
      toast.error(t('process.saveFailed'), e instanceof Error ? e.message : undefined)
    }
  }

  // ---- spot weight ----
  const [spot, setSpot] = useState<number | null>(null)
  const saveSpot = async () => {
    if (due === null || spot === null || spot <= 0) return
    try {
      const payload: WeighPouchesPayload = {
        run_id: run.id,
        pack_lot_id: lot.id,
        weights: [{ value_g: spot, occurred_at: new Date().toISOString(), position: due }],
        method: 'MANUAL_ENTRY',
        equipment_id: null,
      }
      await enqueueCommand({ type: 'weigh_pouches', payload, actorId: actor.user_id, parentIds: runParentIds(run) })
      if (isBelowMinimum(spot, sku.net_g, sku.tolerance_g)) update((d) => ({ ...d, pending_reject: true }))
      setSpot(null)
    } catch (e) {
      toast.error(t('process.saveFailed'), e instanceof Error ? e.message : undefined)
    }
  }

  // ---- end of run ----
  const defaultSack = state.pack.sack_types.find((s) => s.default_output) ?? state.pack.sack_types[0]
  const sackTypeId = fill.sack_type_id ?? defaultSack?.id ?? null
  const sackType = state.pack.sack_types.find((s) => s.id === sackTypeId)
  const usedHere = new Set(
    [fill.leftover_tag?.code, fill.spillage_tag?.code].filter((x): x is string => !!x),
  )
  const sacksLeft = held ? 0 : Math.max(0, issued - run.inputs.length)
  const endReady =
    run.inputs.length > 0 &&
    sacksLeft === 0 &&
    filling &&
    fill.pouches_good > 0 &&
    !!run.last_pouch &&
    fill.leftover_g !== null &&
    fill.spillage_g !== null &&
    (fill.leftover_g === 0 || (!!fill.leftover_tag && fill.leftover_g > (sackType?.tare_g ?? 0))) &&
    (fill.spillage_g === 0 || (!!fill.spillage_tag && fill.spillage_g > (sackType?.tare_g ?? 0))) &&
    !!sackType &&
    !fill.pending_reject
  const [finishing, setFinishing] = useState(false)
  const finish = async () => {
    if (!endReady || !sackType) return
    setFinishing(true)
    try {
      const sacks: Array<{ key: 'LEFTOVER' | 'SPILLAGE'; g: number; tag: TagScan }> = []
      if (fill.leftover_g && fill.leftover_tag) sacks.push({ key: 'LEFTOVER', g: fill.leftover_g, tag: fill.leftover_tag })
      if (fill.spillage_g && fill.spillage_tag) sacks.push({ key: 'SPILLAGE', g: fill.spillage_g, tag: fill.spillage_tag })
      const commands: EnqueueInput[] = sacks.map((s, i) => {
        const container_id = newId()
        const payload: RecordOutputPayload = {
          container_id,
          unit_id: newId(),
          run_id: run.id,
          qr_payload: s.tag.code,
          entry: s.tag.entry,
          stream_key: s.key,
          grade: null,
          sack_type_id: sackType.id,
          tare_g: sackType.tare_g,
          weight_read_g: s.g,
          confirmed_twice: false,
          moisture_pct: null,
          tie: s.key === 'SPILLAGE' ? 'CONV' : 'NONE',
        }
        return {
          id: container_id,
          type: 'record_output',
          payload,
          actorId: actor.user_id,
          ...(i === 0 ? { parentIds: runParentIds(run) } : {}),
        }
      })
      if (commands.length) await enqueueCommands(commands)
      update((d) => ({ ...d, finished: true }))
    } catch (e) {
      toast.error(t('process.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setFinishing(false)
    }
  }

  const tagField = (which: 'leftover_tag' | 'spillage_tag') => (
    <ScanField
      value={fill[which]?.code ?? null}
      validate={(c) => {
        const v = checkFreshCode(state, c, 'OUTPUT_BAG', actor.user_id, usedHere)
        return v.ok ? null : refusal(v.reason)
      }}
      onChange={(c, meta) =>
        update((d) => ({ ...d, [which]: c ? { code: c.trim().toUpperCase(), entry: meta?.entry ?? 'SCAN' } : null }))
      }
    />
  )

  return (
    <div className="flex flex-col gap-4">
      {dayOver && <Banner tone="error" title={t('pack.fill.dayOverTitle')}>{t('pack.fill.dayOverBody')}</Banner>}
      {stopLine && <Banner tone="error" title={both('pack.fill.stopLineTitle')}>{t('pack.fill.stopLineBody')}</Banner>}

      {/* 1. Sacks into the hopper */}
      <Card>
        <CardHeader eyebrow={<BilingualLabel k="pack.fill.sacksTitle" />} />
        <CardBody className="flex flex-col gap-3">
          <div className="flex gap-2">
            <Counter label={t('pack.fill.sacksIn')} value={run.inputs.length} total={issued || undefined} tone={issued > 0 && run.inputs.length >= issued ? 'ok' : 'neutral'} />
            <Counter label={t('pack.fill.kgIn')} value={Math.round(inputG / 1000)} />
          </div>
          {!sack ? (
            <ScanButton
              continuous={false}
              variant={run.inputs.length ? 'secondary' : 'primary'}
              disabled={dayOver}
              label={<BilingualLabel k="pack.fill.scanSack" />}
              validate={(c) => {
                const v = checkPackSack(state, run, c)
                return v.ok ? null : refusal(v.reason)
              }}
              onScan={(c, meta) => {
                const v = checkPackSack(state, run, c)
                if (v.ok) setSack({ bag: v.value, qr: c.trim().toUpperCase(), entry: meta.entry })
              }}
            />
          ) : (
            <div className="flex flex-col gap-3 rounded-card border border-ink p-3">
              <div className="font-mono text-[16px] font-bold">{sack.qr}</div>
              <Line label={t('pack.fill.machineWeight')} value={fmt.weight(sack.bag.net_g_current ?? 0)} />
              <ReadingField
                label={<BilingualLabel k="pack.fill.sackWeight" />}
                hint={t('process.weighOut.weightHint', { tare: fmt.weight(sack.bag.tare_g) })}
                needsTwice={(g) =>
                  sackNeedsSecondEntry(g, sack.bag.tare_g, sack.bag.net_g_current, state.pack.settings.pack_sack_margin_g)
                }
                onChange={setSackReading}
              />
              <Button size="xl" fullWidth disabled={!sackReading} onClick={() => void sackIn()}>
                <BilingualLabel k="pack.fill.sackIn" />
              </Button>
              <Button variant="ghost" size="touch" onClick={() => setSack(null)}>
                {t('common.cancel')}
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      {/* 2. First pouch */}
      <Card>
        <CardHeader
          eyebrow={<BilingualLabel k="pack.fill.firstTitle" />}
          actions={run.first_pouch?.match ? <CheckCircle className="text-[22px] text-ok" weight="fill" aria-hidden="true" /> : undefined}
        />
        <CardBody className="flex flex-col gap-3">
          {run.first_pouch?.match ? (
            <p className="text-[14px] text-ok">{t('pack.fill.firstOk')}</p>
          ) : (
            <>
              {run.first_pouch && !run.first_pouch.match && (
                <Banner tone="error">{both('pack.fill.firstBad')}</Banner>
              )}
              {run.inputs.length === 0 && <p className="text-[13px] text-muted">{t('pack.fill.firstNeedsSack')}</p>}
              <Field label={<BilingualLabel k="pack.fill.firstScan" />} required>
                <ScanField
                  value={pouchText?.position === 'FIRST' ? pouchText.text : null}
                  disabled={run.inputs.length === 0}
                  normalize={(c) => c.trim()}
                  validate={(c) => (checkPouchText(c.trim(), lot.qr_url, isKnownTag) === 'SACK_TAG' ? both('pack.fill.sackNotPouch') : null)}
                  onChange={(c) => setPouchText(c ? { position: 'FIRST', text: c.trim() } : null)}
                />
              </Field>
              <Field label={<BilingualLabel k="pack.fill.firstPhoto" />} required>
                <PhotoCapture value={pouchText?.position === 'FIRST' ? pouchPhoto : null} onChange={setPouchPhoto} disabled={pouchText?.position !== 'FIRST'} />
              </Field>
              <Button size="touch" disabled={pouchText?.position !== 'FIRST' || !pouchPhoto} onClick={() => void verify('FIRST')}>
                <BilingualLabel k="pack.fill.check" />
              </Button>
            </>
          )}
        </CardBody>
      </Card>

      {/* 3. Count and spot weights */}
      <Card>
        <CardHeader eyebrow={<BilingualLabel k="pack.fill.countTitle" />} />
        <CardBody className="flex flex-col gap-3">
          <div className="flex gap-2">
            <Counter label={<BilingualLabel k="pack.fill.good" />} value={fill.pouches_good} total={run.planned_pouches} />
            <Counter label={<BilingualLabel k="pack.fill.rejected" />} value={fill.pouches_rejected} tone={fill.pending_reject ? 'warn' : 'neutral'} />
          </div>
          {!filling && <p className="text-[13px] text-muted">{t('pack.fill.lockedUntilFirst')}</p>}
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <Button
              size="xl"
              disabled={countLocked}
              leadingIcon={<Plus aria-hidden="true" weight="bold" />}
              onClick={() => update((d) => ({ ...d, pouches_good: d.pouches_good + sku.pouches_per_carton }))}
            >
              <BilingualLabel k="pack.fill.carton" vars={{ n: sku.pouches_per_carton }} />
            </Button>
            <Button
              size="xl"
              variant="secondary"
              aria-label={t('pack.fill.cartonLess')}
              disabled={!filling || fill.pouches_good < sku.pouches_per_carton}
              onClick={() => update((d) => ({ ...d, pouches_good: Math.max(0, d.pouches_good - sku.pouches_per_carton) }))}
            >
              <Minus aria-hidden="true" weight="bold" />
            </Button>
          </div>
          <Field label={t('pack.fill.typeCount')}>
            <IntegerInput
              value={fill.pouches_good || null}
              disabled={!filling || fill.pending_reject || dayOver}
              onChange={(n) => update((d) => ({ ...d, pouches_good: n ?? 0 }))}
            />
          </Field>
          <Button
            size="touch"
            variant={fill.pending_reject ? 'danger' : 'secondary'}
            disabled={!filling}
            onClick={() => update((d) => ({ ...d, pouches_rejected: d.pouches_rejected + 1, pending_reject: false }))}
          >
            <BilingualLabel k="pack.fill.reject" />
          </Button>
          {fill.pending_reject && <Banner tone="error">{both('pack.fill.lowPouch')}</Banner>}

          {due !== null && (
            <div className="flex flex-col gap-3 rounded-card border-2 border-amber p-3">
              <BilingualLabel k="pack.fill.spotTitle" vars={{ n: due }} className="font-bold" />
              <div className="text-center">
                <div className="text-[12px] text-muted">{t('pack.fill.minWeight')}</div>
                <div className="font-mono text-[40px] font-bold leading-none">{minG} g</div>
              </div>
              <Field label={<BilingualLabel k="pack.fill.spotWeight" />} required>
                <IntegerInput value={spot} onChange={setSpot} maxDigits={5} />
              </Field>
              {spot !== null && spot > 0 && isBelowMinimum(spot, sku.net_g, sku.tolerance_g) && (
                <Banner tone="error">{t('pack.fill.belowMin')}</Banner>
              )}
              <Button size="xl" fullWidth disabled={spot === null || spot <= 0} onClick={() => void saveSpot()}>
                <BilingualLabel k="pack.fill.next" />
              </Button>
            </div>
          )}
          {run.spot_weights.length > 0 && (
            <p className="text-[13px] text-muted">
              {t('pack.fill.spotsDone', { n: run.spot_weights.length, low: Math.round(lowPct) })}
            </p>
          )}
        </CardBody>
      </Card>

      {/* 4. Last pouch */}
      <Card>
        <CardHeader
          eyebrow={<BilingualLabel k="pack.fill.lastTitle" />}
          actions={run.last_pouch ? <CheckCircle className={run.last_pouch.match ? 'text-[22px] text-ok' : 'text-[22px] text-warn'} weight="fill" aria-hidden="true" /> : undefined}
        />
        <CardBody className="flex flex-col gap-3">
          {run.last_pouch && !run.last_pouch.match && <Banner tone="warn">{t('pack.fill.lastBad')}</Banner>}
          {run.last_pouch?.match && <p className="text-[14px] text-ok">{t('pack.fill.lastOk')}</p>}
          {!run.last_pouch?.match && (
            <>
              <Field label={<BilingualLabel k="pack.fill.lastScan" />} required>
                <ScanField
                  value={pouchText?.position === 'LAST' ? pouchText.text : null}
                  disabled={!filling || fill.pouches_good === 0}
                  normalize={(c) => c.trim()}
                  validate={(c) => (checkPouchText(c.trim(), lot.qr_url, isKnownTag) === 'SACK_TAG' ? both('pack.fill.sackNotPouch') : null)}
                  onChange={(c) => setPouchText(c ? { position: 'LAST', text: c.trim() } : null)}
                />
              </Field>
              <Field label={<BilingualLabel k="pack.fill.lastPhoto" />} required>
                <PhotoCapture value={pouchText?.position === 'LAST' ? pouchPhoto : null} onChange={setPouchPhoto} disabled={pouchText?.position !== 'LAST'} />
              </Field>
              <Button size="touch" disabled={pouchText?.position !== 'LAST' || !pouchPhoto} onClick={() => void verify('LAST')}>
                <BilingualLabel k="pack.fill.check" />
              </Button>
            </>
          )}
        </CardBody>
      </Card>

      {/* 5. Leftover and spillage */}
      <Card>
        <CardHeader eyebrow={<BilingualLabel k="pack.fill.endTitle" />} />
        <CardBody className="flex flex-col gap-3">
          <Field label={<BilingualLabel k="pack.fill.sackType" />} required>
            <ChoiceGroup
              layout="stack"
              options={state.pack.sack_types.map((s) => ({ value: s.id, label: `${s.name} · ${fmt.weight(s.tare_g)}` }))}
              value={sackTypeId}
              onChange={(v) => update((d) => ({ ...d, sack_type_id: v }))}
            />
          </Field>
          <Field label={<BilingualLabel k="pack.fill.leftover" />} hint={t('pack.fill.zeroIfNone')} required>
            <KgInput value={fill.leftover_g} onChange={(g) => update((d) => ({ ...d, leftover_g: g }))} />
          </Field>
          {(fill.leftover_g ?? 0) > 0 && (
            <Field label={<BilingualLabel k="pack.fill.leftoverTag" />} required>
              {tagField('leftover_tag')}
            </Field>
          )}
          <Field label={<BilingualLabel k="pack.fill.spillage" />} hint={t('pack.fill.zeroIfNone')} required>
            <KgInput value={fill.spillage_g} onChange={(g) => update((d) => ({ ...d, spillage_g: g }))} />
          </Field>
          {(fill.spillage_g ?? 0) > 0 && (
            <>
              <Banner tone="error">{both('process.tie.redBanner')}</Banner>
              <Field label={<BilingualLabel k="pack.fill.spillageTag" />} required>
                {tagField('spillage_tag')}
              </Field>
            </>
          )}
        </CardBody>
      </Card>

      {held && <Banner tone="warn">{t('pack.fill.heldFinish')}</Banner>}
      {sacksLeft > 0 && run.inputs.length > 0 && <Banner tone="info">{t('pack.fill.sacksLeft', { n: sacksLeft })}</Banner>}
      <Button size="xl" fullWidth disabled={!endReady} loading={finishing} onClick={() => void finish()}>
        <BilingualLabel k="pack.fill.finished" />
      </Button>
    </div>
  )
}
